import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { XMarkIcon } from '@heroicons/react/24/outline';
import frameService from '../../api/frameService';
import { toast } from 'react-toastify';

const DraggableFrameItem = ({ frame, isDisabled }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: `frame-template-${frame.id}`,
        data: {
            type: 'FRAME_TEMPLATE',
            frameConfig: frame,
        },
        disabled: isDisabled,
    });

    const style = {
        opacity: isDragging ? 0.5 : (isDisabled ? 0.5 : 1),
        cursor: isDisabled ? 'not-allowed' : 'grab',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...listeners}
            {...attributes}
            className={`bg-white border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow ${isDisabled ? 'border-gray-200' : 'border-gray-200 hover:border-blue-400'}`}
        >
            <div className="h-24 bg-gray-50 mb-2 flex items-center justify-center rounded border border-gray-100 overflow-hidden">
                {frame.svgUrl ? (
                    <img src={frame.svgUrl} alt={frame.name} className="h-full w-full object-contain pointer-events-none" />
                ) : (
                    <span className="text-gray-400 text-xs">No Image</span>
                )}
            </div>
            <div className="text-sm font-medium text-gray-800">{frame.name}</div>
        </div>
    );
};

const FrameSelectionPanel = ({ onClose, isFramePlaced }) => {
    const { t } = useTranslation();
    const [frames, setFrames] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchFrames = async () => {
            try {
                const response = await frameService.getAllFrames();
                setFrames(response.data);
            } catch (error) {
                console.error('Error fetching frames:', error);
                toast.error("Не вдалося завантажити каркаси");
            } finally {
                setIsLoading(false);
            }
        };

        fetchFrames();
    }, []);

    if (isLoading) {
        return <div className="p-4 text-center text-gray-500">Завантаження...</div>;
    }

    return (
        <div className="h-full flex flex-col bg-white border-r border-gray-200 shadow-xl z-20 w-80">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                <div>
                    <h3 className="font-semibold text-gray-800">Вибір каркасу</h3>
                    <p className="text-xs text-gray-500 mt-1">Перетягніть каркас на сітку</p>
                </div>
                {onClose && (
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                        title={t('moduleTemplatePanel.close')}
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {isFramePlaced && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 text-sm text-blue-800">
                        Каркас вже розміщено. Видаліть його, щоб додати новий.
                    </div>
                )}

                {frames.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                        Немає доступних каркасів.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-3">
                        {frames.map((frame) => (
                            <DraggableFrameItem
                                key={frame.id}
                                frame={frame}
                                isDisabled={isFramePlaced}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FrameSelectionPanel;
